import { useTranslate } from '../../application/i18n/LocaleProvider';
import { carName } from '../../domain/cars/carCatalogue';
import { BackIcon, ForwardIcon } from '../icons/ControlIcons';

/**
 * The deck, operated rather than swiped (NFR-04).
 *
 * A gesture is not an interface on its own: it cannot be reached by keyboard, it
 * cannot be announced, and it cannot be performed by anybody using a switch. So
 * every step the swipe can make, these two buttons can make — and they name the
 * car they lead to rather than a direction, because "Next" says nothing about
 * where it goes while "Next: Porsche 959" is the deck read aloud.
 *
 * The POSITION LINE sits between them. It is printed as well as announced,
 * because a stack of slivers says there is more of this and says nothing at all
 * about how much more; "4 of 12" is the difference between browsing and
 * wandering.
 *
 * Never disabled. The deck is a ring — `deckStep` wraps — so both directions
 * always go somewhere, and a control that could be disabled here would only be
 * so in a shelf of one, where the whole bar is pointless and the screen simply
 * does not draw it.
 */
export default function CarDeckControls({ deck, onStep }) {
    const t = useTranslate();

    if (deck.total < 2) {
        return null;
    }

    return (
        // `data-gesture="grab"` is the escape hatch `isSidewaysClaimed` already
        // provides, and this bar needs it for the reason the card's own stage
        // does: a control claims sideways by default — a drag that begins on a
        // button is not a page gesture — and these two float over the artwork,
        // which IS the swipe surface. Without it, a swipe that happens to start
        // where an arrow is drawn does nothing at all, and a reader has no way
        // to know why.
        <div className="deck-controls" data-gesture="grab">
            <button
                type="button"
                className="round-button"
                onClick={() => onStep('back')}
                aria-label={t('cars.deck.previous', { car: carName(deck.previous) })}
            >
                <BackIcon size={20} />
            </button>

            {/* Not a live region. It changes on every step, and a step is always
                accompanied by the card's own `nowShowing` announcement — two
                announcements per swipe is one too many. */}
            <p className="deck-controls__position figure">
                {t('cars.deck.position', { index: deck.index + 1, total: deck.total })}
            </p>

            <button
                type="button"
                className="round-button"
                onClick={() => onStep('forward')}
                aria-label={t('cars.deck.next', { car: carName(deck.next) })}
            >
                <ForwardIcon size={20} />
            </button>
        </div>
    );
}
